import React, { useEffect, useState } from 'react';
import ProfilePic from '../../components/profilepic/ProfilePic';
import ApiService from '../../services/ApiService';
import AdminLayout from './AdminLayout';
import './AdminPage.css';

const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Дата неизвестна' : date.toLocaleString('ru-RU');
};

const getRegionDisplay = (value) => {
    const code = String(value || '').trim().toUpperCase();
    if (!code) return null;

    const normalizedFlagCode = code === 'UK' ? 'GB' : code;
    const flagCode = /^[A-Z]{2}$/.test(normalizedFlagCode) ? normalizedFlagCode.toLowerCase() : '';

    return { code, flagCode };
};

const AdminUsersPage = () => {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [error, setError] = useState('');
    const [busyUserId, setBusyUserId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await ApiService.getUsers(page);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка загрузки');
            setUsers(Array.isArray(response.data) ? response.data : []);
        } catch (requestError) {
            console.error('Error fetching users:', requestError);
            setUsers([]);
            setError('Не удалось загрузить пользователей.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Пользователи — админ-панель';
        fetchData();
    }, [page]);

    const handleBanToggle = async (user) => {
        const action = user.banned ? 'разблокировать' : 'заблокировать';
        if (!window.confirm(`${action[0].toUpperCase()}${action.slice(1)} пользователя ${user.nickname}?`)) return;
        setBusyUserId(user.id);
        try {
            const response = await ApiService.banUser(user.id, !user.banned);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка операции');
            await fetchData();
        } catch (requestError) {
            console.error('Error changing user status:', requestError);
            setError('Не удалось изменить статус пользователя.');
        } finally {
            setBusyUserId(null);
        }
    };

    const renderPagination = () => (
        <div className="admin-pagination">
            <span>Страница {page + 1}</span>
            <div className="admin-pagination-controls">
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page === 0}>Назад</button>
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => setPage((current) => current + 1)}>Дальше</button>
                <input
                    type="number"
                    min="1"
                    aria-label="Номер страницы"
                    value={page + 1}
                    onChange={(event) => setPage(Math.max(0, Number(event.target.value || 1) - 1))}
                />
            </div>
        </div>
    );

    return (
        <AdminLayout
            mainClassName="admin-list-shell"
            eyebrow="Сообщество"
            title="Пользователи"
            lead="Аккаунты, регионы и статусы блокировки."
            actions={<button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={fetchData} disabled={loading}>Обновить</button>}
        >
            {renderPagination()}
            {loading ? <div className="admin-state">Загрузка пользователей…</div> : null}
            {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
            {!loading && !error && users.length === 0 ? <div className="admin-state">На этой странице нет пользователей.</div> : null}

            <div className="admin-record-list">
                {users.map((user) => {
                    const region = getRegionDisplay(user.region);
                    return (
                        <article key={user.id} className={`admin-user-row${user.banned ? ' banned' : ''}`}>
                            <ProfilePic isBig={false} account={user} />
                            <div className="admin-user-identity">
                                <strong>{user.nickname || 'Без ника'}</strong>
                                <span>{user.banned ? 'Заблокирован' : 'Активен'} · ID {user.id}</span>
                            </div>
                            <div className="admin-user-meta">
                                <span>Регистрация: {formatDate(user.registrationDate)}</span>
                                {region ? (
                                    <span className="admin-region-badge" title={`Регион: ${region.code}`}>
                                        {region.flagCode ? (
                                            <img
                                                className="admin-region-flag"
                                                src={`https://flagcdn.com/24x18/${region.flagCode}.png`}
                                                alt=""
                                                aria-hidden="true"
                                                loading="lazy"
                                                onError={(event) => { event.currentTarget.hidden = true; }}
                                            />
                                        ) : null}
                                        <span>{region.code}</span>
                                    </span>
                                ) : null}
                            </div>
                            <div className="admin-row-actions">
                                <a className="ui-btn ui-btn--secondary ui-btn--sm admin-button" href={`/profile/${encodeURIComponent(user.nickname)}`}>Профиль</a>
                                <button
                                    type="button"
                                    className={user.banned ? 'ui-btn ui-btn--secondary ui-btn--sm admin-button' : 'ui-btn ui-btn--sm admin-danger-button'}
                                    onClick={() => handleBanToggle(user)}
                                    disabled={busyUserId === user.id}
                                >
                                    {busyUserId === user.id ? 'Обновление…' : user.banned ? 'Разбокировать' : 'Заблокировать'}
                                </button>
                            </div>
                        </article>
                    );
                })}
            </div>
            {renderPagination()}
        </AdminLayout>
    );
};

export default AdminUsersPage;
